import React from "react";
import {
    Alert,
    Autocomplete,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import AppDatePicker from "../../shared/AppDatePicker";
import {
    CUSTOMER_SELECT_EMPTY_HELPER,
    CUSTOMER_SELECT_LABEL,
    CUSTOMER_SELECT_LOADING,
    CUSTOMER_SELECT_NO_OPTIONS,
    CUSTOMER_SELECT_PLACEHOLDER,
    getCustomerOptionLabel,
    isCustomerOptionEqualToValue,
} from "../../shared/customerSelectUtils";
import { frequencyOptions } from "./recurringInvoiceUtils";

export default function RecurringInvoiceDialog({
    open,
    mode,
    form,
    saving,
    fieldErrors,
    articleErrors,
    customerOptions,
    customersLoading,
    selectedCustomerOption,
    onClose,
    onFormChange,
    onCustomerChange,
    onCustomerInputChange,
    onTemplateDateChange,
    onAddArticle,
    onRemoveArticle,
    onTemplateChange,
    onSave,
    sortedArticles,
    profil,
}) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{mode === "edit" ? "Abo bearbeiten" : "Neues Abo anlegen"}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Grid2 container spacing={2}>
                        <Grid2 xs={12} md={6}>
                            <TextField
                                label="Titel"
                                fullWidth
                                value={form.title}
                                onChange={(event) => onFormChange({ title: event.target.value })}
                            />
                        </Grid2>
                        <Grid2 xs={12} md={6}>
                            <Autocomplete
                                options={customerOptions}
                                loading={customersLoading}
                                value={selectedCustomerOption}
                                onChange={onCustomerChange}
                                onInputChange={onCustomerInputChange}
                                getOptionLabel={getCustomerOptionLabel}
                                isOptionEqualToValue={isCustomerOptionEqualToValue}
                                loadingText={CUSTOMER_SELECT_LOADING}
                                noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                                sx={{ minWidth: 200 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={CUSTOMER_SELECT_LABEL}
                                        placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                        error={Boolean(fieldErrors.customerId)}
                                        helperText={
                                            fieldErrors.customerId ||
                                            (!customersLoading && customerOptions.length === 0
                                                ? CUSTOMER_SELECT_EMPTY_HELPER
                                                : "")
                                        }
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {customersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                fullWidth
                            />
                        </Grid2>
                        <Grid2 xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel id="frequency-label">Periode</InputLabel>
                                <Select
                                    labelId="frequency-label"
                                    label="Periode"
                                    value={form.frequency}
                                    onChange={(event) => onFormChange({ frequency: event.target.value })}
                                >
                                    {frequencyOptions.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid2>
                        <Grid2 xs={12} md={4}>
                            <AppDatePicker
                                label="Nächste Ausführung"
                                fullWidth
                                value={form.nextRun || ""}
                                onChange={(date) => onFormChange({ nextRun: date })}
                                error={Boolean(fieldErrors.nextRun)}
                                helperText={fieldErrors.nextRun || ""}
                            />
                        </Grid2>
                        <Grid2 xs={12} md={4}>
                            <AppDatePicker
                                label="Ende (optional)"
                                fullWidth
                                value={form.endDate || ""}
                                onChange={(date) => onFormChange({ endDate: date })}
                                error={Boolean(fieldErrors.endDate)}
                                helperText={fieldErrors.endDate || ""}
                            />
                        </Grid2>
                    </Grid2>

                    <Divider textAlign="left">Automatisierung</Divider>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.autoSendMail}
                                    onChange={(event) => onFormChange({ autoSendMail: event.target.checked })}
                                />
                            }
                            label="Automatisch per E-Mail versenden"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.includePayLink}
                                    onChange={(event) => onFormChange({ includePayLink: event.target.checked })}
                                />
                            }
                            label="Pay-Link generieren"
                        />
                    </Stack>
                    <Grid2 container spacing={2}>
                        <Grid2 xs={12} md={6}>
                            <TextField
                                label="Zahlungsmethode (Hinweis im E-Mail-Text)"
                                fullWidth
                                value={form.paymentMethod}
                                onChange={(event) => onFormChange({ paymentMethod: event.target.value })}
                            />
                        </Grid2>
                        <Grid2 xs={12} md={6}>
                            <TextField
                                label="E-Mail Betreff (Platzhalter: {{invoiceNumber}})"
                                fullWidth
                                value={form.mailSubject}
                                onChange={(event) => onFormChange({ mailSubject: event.target.value })}
                            />
                        </Grid2>
                        <Grid2 xs={12}>
                            <TextField
                                label="E-Mail Text"
                                fullWidth
                                multiline
                                minRows={4}
                                helperText="Platzhalter: {{invoiceNumber}}, {{invoiceDate}}, {{dueDate}}, {{total}}, {{payLink}}"
                                value={form.mailBody}
                                onChange={(event) => onFormChange({ mailBody: event.target.value })}
                            />
                        </Grid2>
                    </Grid2>

                    <Divider textAlign="left">Rechnungsvorlage</Divider>
                    <Grid2 container spacing={2}>
                        <Grid2 xs={12} md={4}>
                            <AppDatePicker
                                label="Rechnungsdatum"
                                fullWidth
                                value={form.template.invoiceDate || ""}
                                onChange={(date) => onTemplateDateChange("invoiceDate", date)}
                                helperText={fieldErrors.invoiceDate || "Optional, Standard ist Ausführungsdatum"}
                                error={Boolean(fieldErrors.invoiceDate)}
                            />
                        </Grid2>
                        <Grid2 xs={12} md={4}>
                            <AppDatePicker
                                label="Fälligkeitsdatum"
                                fullWidth
                                value={form.template.dueDate || ""}
                                onChange={(date) => onTemplateDateChange("dueDate", date)}
                                helperText={fieldErrors.dueDate || "Optional, Standard ist +14 Tage"}
                                error={Boolean(fieldErrors.dueDate)}
                            />
                        </Grid2>
                        <Grid2 xs={12} md={4}>
                            <AppDatePicker
                                label="Leistungsdatum"
                                fullWidth
                                value={form.template.performanceDate || ""}
                                onChange={(date) => onTemplateDateChange("performanceDate", date)}
                                error={Boolean(fieldErrors.performanceDate)}
                                helperText={fieldErrors.performanceDate || ""}
                            />
                        </Grid2>
                    </Grid2>
                    <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="subtitle1">Artikel / Positionen</Typography>
                            <Button startIcon={<AddRoundedIcon />} onClick={onAddArticle}>
                                Position hinzufügen
                            </Button>
                        </Stack>
                        {fieldErrors.articles && <Alert severity="error">{fieldErrors.articles}</Alert>}
                        {(form.template.articles || []).map((item, index) => (
                            <Card key={index} variant="outlined">
                                <CardContent>
                                    <Grid2 container spacing={2}>
                                        <Grid2 xs={12} md={5}>
                                            <FormControl fullWidth error={Boolean(articleErrors[index]?.articleId)}>
                                                <InputLabel id={`article-${index}`}>Artikel</InputLabel>
                                                <Select
                                                    labelId={`article-${index}`}
                                                    label="Artikel"
                                                    value={item.articleId}
                                                    onChange={(event) =>
                                                        onTemplateChange(index, { articleId: event.target.value })
                                                    }
                                                >
                                                    {sortedArticles.map((article) => {
                                                        const net = Number(article.price ?? 0);
                                                        const gross = net * (1 + Number(article.taxRate || 0) / 100);
                                                        const label = profil?.kleinunternehmer
                                                            ? `${article.name || ""} – ${net.toFixed(2)} €`
                                                            : `${article.name || ""} – ${net.toFixed(2)} € netto (${gross.toFixed(2)} € brutto)`;
                                                        return (
                                                            <MenuItem key={article.id} value={article.id}>
                                                                {label || article.id}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                                {articleErrors[index]?.articleId && (
                                                    <FormHelperText>{articleErrors[index].articleId}</FormHelperText>
                                                )}
                                            </FormControl>
                                        </Grid2>
                                        <Grid2 xs={6} md={2}>
                                            <TextField
                                                label="Menge"
                                                fullWidth
                                                value={item.amount}
                                                onChange={(event) => onTemplateChange(index, { amount: event.target.value })}
                                                error={Boolean(articleErrors[index]?.amount)}
                                                helperText={articleErrors[index]?.amount || ""}
                                            />
                                        </Grid2>
                                        <Grid2 xs={6} md={2}>
                                            <TextField
                                                label="Rabatt %"
                                                fullWidth
                                                value={item.articleDiscount}
                                                onChange={(event) =>
                                                    onTemplateChange(index, { articleDiscount: event.target.value })
                                                }
                                                error={Boolean(articleErrors[index]?.articleDiscount)}
                                                helperText={articleErrors[index]?.articleDiscount || ""}
                                            />
                                        </Grid2>
                                        <Grid2 xs={12} md={3}>
                                            <TextField
                                                label="Rabatt-Begründung"
                                                fullWidth
                                                value={item.articleDiscountReason}
                                                onChange={(event) =>
                                                    onTemplateChange(index, { articleDiscountReason: event.target.value })
                                                }
                                                error={Boolean(articleErrors[index]?.articleDiscountReason)}
                                                helperText={articleErrors[index]?.articleDiscountReason || ""}
                                            />
                                        </Grid2>
                                    </Grid2>
                                </CardContent>
                                <CardActions>
                                    <Button startIcon={<CloseRoundedIcon />} color="error" onClick={() => onRemoveArticle(index)}>
                                        Entfernen
                                    </Button>
                                </CardActions>
                            </Card>
                        ))}
                        {!(form.template.articles || []).length && (
                            <Alert severity="info">Noch keine Artikel hinzugefügt.</Alert>
                        )}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
                <Button onClick={onSave} variant="contained" disabled={saving}>
                    {saving ? "Speichere…" : "Speichern"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
